import { useCallback, useSyncExternalStore } from 'react'
import type { Session } from '@/types/auth'

// The one place localStorage is read/written for auth. Every other file goes
// through this module (the hook, or the plain functions below), never
// localStorage directly - so the storage mechanism can change later in one
// place.
const STORAGE_KEY = 'legalflow-config-session'

const listeners = new Set<() => void>()

function parseSession(raw: string | null): Session | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as Session
  } catch {
    return null
  }
}

// Cached so useSyncExternalStore's getSnapshot returns a stable reference
// when nothing has changed (re-parsing JSON on every call would return a new
// object each time and cause React to warn/loop).
let cachedSession: Session | null = parseSession(localStorage.getItem(STORAGE_KEY))

function getSnapshot(): Session | null {
  return cachedSession
}

function writeSession(session: Session | null) {
  cachedSession = session
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Plain functions for non-React callers (apiRequest, which reads the token
// fresh on every call rather than a stale closed-over value).
export function getStoredToken(): string | null {
  return cachedSession?.token ?? null
}

export function clearStoredSession() {
  writeSession(null)
}

export function useAuthToken() {
  const session = useSyncExternalStore(subscribe, getSnapshot)

  const setSession = useCallback((next: Session) => {
    writeSession(next)
  }, [])

  const clearSession = useCallback(() => {
    writeSession(null)
  }, [])

  return {
    token: session?.token ?? null,
    email: session?.email ?? null,
    setSession,
    clearSession,
  }
}
